import React, { useState } from 'react';

function ApiInstructionsModal({ isOpen, onClose, userId, apiEndpoints }) {
  const [testResponse, setTestResponse] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const testEndpoint = async (endpoint) => {
    setIsLoading(true);
    setError(null);
    setTestResponse(null);
    
    try {
      if (endpoint === apiEndpoints.singleEndpoint) {
        // First fetch the list to get a valid essay ID
        const listResponse = await fetch(apiEndpoints.listEndpoint);
        const essays = await listResponse.json();
        
        if (essays.length === 0) {
          throw new Error('No published essays found to test with');
        }
        
        // Use the first essay's ID
        const firstEssayId = essays[0].id;
        const singleResponse = await fetch(endpoint.replace('ESSAY_ID', firstEssayId));
        const data = await singleResponse.json();
        setTestResponse(data);
      } else {
        const response = await fetch(endpoint);
        const data = await response.json();
        setTestResponse(data);
      }
    } catch (err) {
      setError('Failed to fetch data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const exampleListResponse = {
    "essays": [
      {
        "id": "essay123",
        "title": "Example Essay Title",
        "body": "Essay content goes here...",
        "status": "published",
        "createdAt": "2024-03-20T10:30:00Z",
        "updatedAt": "2024-03-20T10:30:00Z"
      },
      // ... more essays
    ]
  };

  const exampleSingleResponse = {
    "id": "essay123",
    "title": "Example Essay Title",
    "body": "Essay content goes here...",
    "status": "published",
    "createdAt": "2024-03-20T10:30:00Z",
    "updatedAt": "2024-03-20T10:30:00Z"
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-[600px] max-h-[80vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">API Access Instructions</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-6">
            {/* Credentials Section */}
            <div>
              <h3 className="font-bold text-lg mb-2">Your Credentials</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-1">Your User ID:</p>
                  <code className="bg-gray-100 p-2 rounded block overflow-x-auto">
                    {userId}
                  </code>
                </div>
              </div>
            </div>

            {/* Endpoints Section */}
            <div>
              <h3 className="font-bold text-lg mb-2">Available Endpoints</h3>
              <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">List All Published Essays:</p>
                  <code className="bg-gray-100 p-2 rounded block overflow-x-auto">
                    GET {apiEndpoints.listEndpoint}
                  </code>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Get Single Essay:</p>
                  <code className="bg-gray-100 p-2 rounded block overflow-x-auto">
                    GET {apiEndpoints.singleEndpoint}
                  </code>
                  <p className="text-xs text-gray-500 mt-1">
                    Replace ESSAY_ID with the ID of the specific essay
                  </p>
                </div>
              </div>
            </div>

            {/* Updated Usage Examples with Response Format */}
            <div>
              <h3 className="font-bold text-lg mb-2">Usage Examples</h3>
              <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-2">JavaScript/React Example:</p>
                  <pre className="bg-gray-100 p-3 rounded overflow-x-auto text-sm">
                    <code>{`// Fetch all published essays
const response = await fetch('${apiEndpoints.listEndpoint}');
const essays = await response.json();

// Fetch a specific essay
const essayId = 'your-essay-id';
const response = await fetch('${apiEndpoints.singleEndpoint}'.replace('ESSAY_ID', essayId));
const essay = await response.json();`}</code>
                  </pre>
                </div>

                <div>
                  <p className="text-sm text-gray-600 mb-2">Response Format - List Essays:</p>
                  <pre className="bg-gray-100 p-3 rounded overflow-x-auto text-sm">
                    <code>{JSON.stringify(exampleListResponse, null, 2)}</code>
                  </pre>
                </div>

                <div>
                  <p className="text-sm text-gray-600 mb-2">Response Format - Single Essay:</p>
                  <pre className="bg-gray-100 p-3 rounded overflow-x-auto text-sm">
                    <code>{JSON.stringify(exampleSingleResponse, null, 2)}</code>
                  </pre>
                </div>
              </div>
            </div>

            {/* Response Fields Documentation */}
            <div>
              <h3 className="font-bold text-lg mb-2">Response Fields</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left">
                      <th className="pb-2">Field</th>
                      <th className="pb-2">Type</th>
                      <th className="pb-2">Description</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-600">
                    <tr>
                      <td className="py-1 pr-4">id</td>
                      <td className="py-1 pr-4">string</td>
                      <td className="py-1">Unique identifier for the essay</td>
                    </tr>
                    <tr>
                      <td className="py-1 pr-4">title</td>
                      <td className="py-1 pr-4">string</td>
                      <td className="py-1">Essay title</td>
                    </tr>
                    <tr>
                      <td className="py-1 pr-4">body</td>
                      <td className="py-1 pr-4">string</td>
                      <td className="py-1">Main content of the essay</td>
                    </tr>
                    <tr>
                      <td className="py-1 pr-4">status</td>
                      <td className="py-1 pr-4">string</td>
                      <td className="py-1">Will always be "published" for API responses</td>
                    </tr>
                    <tr>
                      <td className="py-1 pr-4">createdAt</td>
                      <td className="py-1 pr-4">string</td>
                      <td className="py-1">ISO timestamp of when the essay was created</td>
                    </tr>
                    <tr>
                      <td className="py-1 pr-4">updatedAt</td>
                      <td className="py-1 pr-4">string</td>
                      <td className="py-1">ISO timestamp of the last update</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Add new Test Endpoints section before Important Notes */}
            <div>
              <h3 className="font-bold text-lg mb-2">Test Endpoints</h3>
              <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => testEndpoint(apiEndpoints.listEndpoint)}
                    className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
                  >
                    Test List Essays Endpoint
                  </button>
                  <button
                    onClick={() => testEndpoint(apiEndpoints.singleEndpoint)}
                    className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
                  >
                    Test Single Essay Endpoint
                  </button>
                </div>

                {isLoading && (
                  <div className="text-gray-600">Loading...</div>
                )}

                {error && (
                  <div className="text-red-500 text-sm">{error}</div>
                )}

                {testResponse && (
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Response:</p>
                    <pre className="bg-gray-100 p-3 rounded overflow-x-auto text-sm">
                      <code>{JSON.stringify(testResponse, null, 2)}</code>
                    </pre>
                  </div>
                )}
              </div>
            </div>

            {/* Important Notes */}
            <div>
              <h3 className="font-bold text-lg mb-2">Important Notes</h3>
              <ul className="list-disc pl-5 space-y-2 text-sm text-gray-600">
                <li>Only published essays will be accessible through the API</li>
                <li>Essays are public and can be accessed by anyone with your user ID</li>
                <li>The API endpoints are read-only</li>
                <li>Consider caching the responses to improve performance</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ApiInstructionsModal; 